import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import VendorLayout from "@/components/vendor_portal/VendorLayout";

const STATUS_STYLES = {
  requested:              { fg: "#7A7A7A", bg: "#F4F3EC", label: "Requested" },
  approved:               { fg: "#1A422D", bg: "#E9F0EB", label: "Approved" },
  ordered_with_vendor:    { fg: "#8a6d2e", bg: "#FFFCEF", label: "With Vendor" },
  ready:                  { fg: "#0F5D3B", bg: "#E9F0EB", label: "Ready" },
  delivered:              { fg: "#3a5c48", bg: "#EAEEE9", label: "Completed" },
  cancelled:              { fg: "#7A7A7A", bg: "#F4F3EC", label: "Cancelled" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.requested;
  return (
    <span className="inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold border"
          style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.fg }}>
      {s.label}
    </span>
  );
}

export default function VendorRequestsPage({ archived = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/vendor/requests", { params: { archived } })
      .then(({ data }) => setRows(data.orders || []))
      .catch((err) => setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [archived]);

  const empty = !loading && rows.length === 0;

  return (
    <VendorLayout>
      <section data-testid="vendor-requests-page">
        <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
          {archived ? "Order History" : "Live Requests"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-3">
          {archived ? "Fulfilled Orders" : "Requests Inbox"}
        </h1>
        <p className="text-sm text-[#7A7A7A] mt-3 max-w-2xl">
          {archived
            ? "Every order you've completed or the club has cancelled. Search history, download packets, and follow up on repeat business."
            : "Every order My Crest Club pro shops have assigned to your account. Confirm receipt, mark items ordered, and message the pro shop in-thread."}
        </p>

        {error && (
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm" data-testid="vendor-requests-error">
            {error}
          </div>
        )}

        {loading && <div className="mt-8 text-sm text-[#7A7A7A]">Loading…</div>}

        {empty && (
          <div className="mt-10 p-10 bg-white border border-[#E5E3D8] max-w-2xl text-center" data-testid="vendor-requests-empty">
            <Inbox size={32} className="mx-auto text-[#B59A5A]" />
            <p className="mt-4 text-[#4A4A4A] leading-relaxed">
              {archived
                ? "No completed orders yet."
                : "No live requests just now. When a My Crest Club pro shop assigns you an order, it will land here instantly."}
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-8 border border-[#E5E3D8] bg-white overflow-x-auto" data-testid="vendor-requests-table">
            <table className="min-w-full">
              <thead className="bg-[#F9F8F4] text-[10px] uppercase tracking-[0.22em] text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-4 py-3">Order</th>
                  <th className="text-left px-4 py-3">Item</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-right px-4 py-3">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE0]">
                {rows.map((o) => {
                  const firstItem = o.item_name || (o.items?.[0]?.item_name || "");
                  const brand = o.brand || o.items?.[0]?.brand;
                  const total = o.items?.length || 1;
                  const assignedCount = o._assigned_line_ids?.length || 0;
                  return (
                    <tr key={o.order_id} className="hover:bg-[#F9F8F4]" data-testid={`vendor-req-row-${o.order_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono text-sm text-[#1A422D]">{o.display_order_id || o.order_id}</div>
                        <div className="text-[10px] text-[#7A7A7A]">{o.club_name || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#1A1A1A]">{firstItem}</div>
                        {brand && <div className="text-[10px] uppercase tracking-wider text-[#B59A5A]">{brand}</div>}
                        {total > 1 && (
                          <div className="text-[10px] text-[#7A7A7A]">
                            {assignedCount > 0 ? `${assignedCount} of ${total} item(s) for you` : `${total} items`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                      <td className="px-4 py-3 text-xs text-[#4A4A4A]">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/vendor/requests/${o.order_id}`}
                              data-testid={`vendor-req-open-${o.order_id}`}
                              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#1A422D] hover:text-[#B59A5A]">
                          Open <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </VendorLayout>
  );
}
